import type { Tag } from "@gettactic/api";
import {
  ArrowUpDown,
  Coffee,
  type LucideIcon,
  Monitor,
  Phone,
  Sun,
  Tag as TagIcon,
  Tv,
  Wifi,
  Zap
} from "lucide-react";
import React from "react";

/**
 * Maps an amenity name to an icon by keyword. The list is intentionally small
 * and falls back to a generic tag icon so unmapped amenities still render.
 */
const amenityIconRules: { keywords: string[]; Icon: LucideIcon }[] = [
  { keywords: ["monitor", "display", "screen"], Icon: Monitor },
  { keywords: ["tv", "television"], Icon: Tv },
  { keywords: ["power", "outlet", "charger", "charging"], Icon: Zap },
  { keywords: ["standing", "adjustable", "sit-stand"], Icon: ArrowUpDown },
  { keywords: ["wifi", "wi-fi", "internet", "ethernet"], Icon: Wifi },
  { keywords: ["window", "natural light"], Icon: Sun },
  { keywords: ["phone", "headset"], Icon: Phone },
  { keywords: ["coffee", "kitchen", "cafe"], Icon: Coffee }
];

const getAmenityIcon = (name: string): LucideIcon => {
  const normalized = name.toLowerCase();
  const rule = amenityIconRules.find((r) =>
    r.keywords.some((keyword) => normalized.includes(keyword))
  );
  return rule?.Icon ?? TagIcon;
};

interface ResourceAmenityChipsProps {
  amenities: Tag[];
  className?: string;
}

/** Renders the resource's amenities as icon + label chips (mockup header row). */
export const ResourceAmenityChips: React.FC<ResourceAmenityChipsProps> = ({
  amenities,
  className
}) => {
  if (!amenities.length) {
    return null;
  }
  return (
    <div className={className}>
      {amenities.map((amenity) => {
        const Icon = getAmenityIcon(amenity.name);
        return (
          <span
            key={amenity.id}
            className="inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-lg border-[0.5px] border-neutral-400/50 border-solid bg-[#f0f4f7] px-3 py-[7px] font-inter font-medium text-[#3a444b] text-[12.5px]"
          >
            <Icon className="h-3.5 w-3.5 text-[#3a444b]" aria-hidden="true" />
            {amenity.name}
          </span>
        );
      })}
    </div>
  );
};
